import type { MeshBuilder, Frame } from '../mesh/meshBuilder';
import { PATTERN } from '../../render/patterns/patternIds';
import { buildHouseRoof } from './houseRoof';
import type { DetailSink } from './detailSink';
import { gableOutline } from './gables';
import { buildCoping, buildFinial, COPING } from './gableTrim';
import { emitFacade, wallFace, type Body } from './houseFacades';
import { houseFrame, houseStyles, type HouseGeometry, type HousePlan } from './houseParts';
import { lotFrame } from './lotFrame';
import { courtyardOpenings, type HouseOpenings, type Opening } from './openings';
import { NO_SHOP } from './shops';
import { CLAVERBLAT } from './surveyed/claverblatPlan';

/** Approximate the archived section; heights and hidden openings remain reconstruction choices. */
export function claverblatOpenings(plan: HousePlan): HouseOpenings {
  const { width, depth } = lotFrame(plan.lot);
  const out: Record<keyof HouseOpenings, Opening[]> = {
    front: [],
    back: [],
    left: [],
    right: [],
    frontGable: [],
    backGable: [],
  };
  let base = 0;
  for (const [i, height] of CLAVERBLAT.quay.floors.entries()) {
    const ordinary = courtyardOpenings(
      {
        width,
        length: depth,
        storeys: 1,
        storeyHeight: height,
        pitch: plan.params.pitch,
        party: plan.party,
        shop: NO_SHOP,
      },
      plan.rng.fork(`claverblat-${i}`)
    );
    for (const side of ['back', 'left', 'right'] as const)
      out[side].push(
        ...ordinary[side]
          .filter((o) => i === 0 || o.kind !== 'door')
          .map((o) => ({ ...o, y: o.y + base }))
      );
    if (i === 0) {
      // Two independent entrances, not two independent gables or roofs.
      for (const x of [width * 0.27, width * 0.73])
        out.front.push({ kind: 'door', x: x - 0.55, y: 0, width: 1.1, height: 3.2, leaf: 2.35 });
    } else {
      const h = Math.min(2.15, height - 1.1),
        w = Math.min(1.1, h / 1.8);
      for (const x of [width * 0.27, width * 0.73])
        out.front.push({ kind: 'window', x: x - w / 2, y: base + 0.65, width: w, height: h });
    }
    base += height;
  }
  for (const side of ['frontGable', 'backGable'] as const)
    out[side].push({
      kind: 'smallWindow',
      x: width / 2 - 0.35,
      y: base + 1.1,
      width: 0.7,
      height: 1.0,
    });
  return out;
}

/** The archived unequal storage floors, with timber end walls under the reconstruction policy. */
export function buildClaverblat(
  builder: MeshBuilder,
  plan: HousePlan,
  details: DetailSink
): HouseGeometry {
  const before = builder.triangleCount;
  const { width, depth } = lotFrame(plan.lot);
  const eaves = CLAVERBLAT.quay.floors.reduce((sum, h) => sum + h, 0);
  const body: Body = {
    x0: 0,
    depth,
    half: width / 2,
    eaves,
    ridge: eaves + CLAVERBLAT.quay.roofRise,
  };
  const frame: Frame = houseFrame(plan.lot);
  const styles = houseStyles(plan, width, depth, eaves);
  const openings = claverblatOpenings(plan);
  const split = CLAVERBLAT.quay.floors[0]!;
  const ctx = { builder, styles, frame, details, edgeId: plan.edgeId };
  for (const [base, top] of [
    [0, split],
    [split, eaves],
  ] as const) {
    const selected = (side: keyof HouseOpenings) =>
      openings[side].filter((o) => o.y >= base && o.y < top).map((o) => ({ ...o, y: o.y - base }));
    const gable = (side: 'frontGable' | 'backGable') =>
      base === 0 ? null : openings[side].map((o) => ({ ...o, y: o.y - base }));
    const size = { width, eaves: top - base, ridge: body.ridge - base };
    const front =
      base === 0
        ? {
            ...ctx,
            styles: { ...styles, wall: { ...styles.wall, pattern: PATTERN.timberFront1538 } },
          }
        : ctx;
    emitFacade(front, wallFace([0, base, -body.half], [-1, 0, 0]), size, {
      wall: selected('front'),
      gable: gable('frontGable'),
    });
    emitFacade(ctx, wallFace([depth, base, body.half], [1, 0, 0]), size, {
      wall: selected('back'),
      gable: gable('backGable'),
    });
    const sideCtx = { ...ctx, styles: { ...styles, wall: styles.side } };
    for (const side of ['left', 'right'] as const)
      emitFacade(
        sideCtx,
        side === 'left'
          ? wallFace([depth, base, -body.half], [0, 0, -1])
          : wallFace([0, base, body.half], [0, 0, 1]),
        { ...size, width: depth },
        { wall: selected(side), gable: null }
      );
  }
  buildHouseRoof(builder, styles, frame, body, plan.params, details);
  const outline = gableOutline('plain', width, eaves, plan.params.pitch);
  buildCoping(builder, styles.trim, frame, outline.coping, {
    xBack: COPING.back,
    halfWidth: body.half,
  });
  buildFinial(builder, styles.trim, frame, outline.apex, COPING.finialRadius);
  return {
    eavesHeight: eaves,
    ridgeHeight: body.ridge,
    triangleCount: builder.triangleCount - before,
  };
}
